import { OrderandoError } from './error-handling';
import { RequestModule } from './order-types';

const STAGING_URL = 'https://staging-api.ando.cloud/v1';
const PROD_URL = 'https://api.ando.cloud/v1';

// const ORDERANDO_API_URL = process.env.NODE_ENV === 'development' ? STAGING_URL : PROD_URL;
const ORDERANDO_API_URL = PROD_URL;

export interface OrderandoBaseResponse<ResponseData> {
  errors?: { code: string; description?: string }[];
  success: boolean;
  data?: ResponseData;
}

interface RequestOptions<TData> {
  data?: TData;
  /**
   * If true, JWT
   * will be excluded from the request
   */
  skipAuth?: boolean;
}

let jwt: string | undefined;
let customerCode = '';

interface SetupOrderandoAPIOptions {
  jwt?: string;
  customerCode: string;
}

export function setupOrderandoAPI({
  jwt: jwtParam,
  customerCode: customerCodeParam,
}: SetupOrderandoAPIOptions) {
  jwt = jwtParam;
  customerCode = customerCodeParam;
}

async function request<TResponse = any, TRequestData = any>(
  module: RequestModule,
  endpoint: string,
  method: 'POST' | 'GET',
  { data, skipAuth = false }: RequestOptions<TRequestData> = {}
): Promise<OrderandoBaseResponse<TResponse>> {
  if (!customerCode) {
    throw new Error('Customer code not defined!');
  }

  let headers: { [key: string]: string } = {
    'content-type': 'application/json',
  };

  if (jwt && !skipAuth) {
    headers = {
      ...headers,
      Authorization: `Bearer ${jwt}`,
    };
  }

  let body: any = undefined;
  let getParams = '';

  if (method === 'POST') {
    body = JSON.stringify(data);
  }

  if (method === 'GET' && data) {
    getParams = `?${Object.keys(data)
      .map((key) => `${key}=${encodeURIComponent((data as any)[key])}`)
      .join('&')}`;
  }

  const requestUrl = `${ORDERANDO_API_URL}/${module}/${customerCode}${endpoint}${getParams}`;

  const response = await fetch(requestUrl, {
    method,
    body,
    headers,
  });

  const reponseData: OrderandoBaseResponse<TResponse> = await response.json();

  if (!reponseData.success || !response.status.toString().startsWith('2')) {
    const errorCode = reponseData.errors?.find((err) => err.code)?.code ?? '0';
    throw new OrderandoError(errorCode);
  }

  return reponseData;
}

export function createPost(module: RequestModule) {
  return function <TResponse = any, TRequestData = any>(
    endpoint: string,
    reqData?: RequestOptions<TRequestData>
  ) {
    return request<TResponse>(module, endpoint, 'POST', reqData);
  };
}

export function createGet(module: RequestModule) {
  return function <TResponse = any, TRequestData = any>(
    endpoint: string,
    reqData?: RequestOptions<TRequestData>
  ) {
    return request<TResponse>(module, endpoint, 'GET', reqData);
  };
}

export function getTimeOffsetMinutes(country = 'Europe/Berlin') {
  const date = new Date();
  const worldTime = new Date(date.toISOString().substr(0, 19)).getTime();
  const localTime = new Date(date.toLocaleString('en', { timeZone: country })).getTime();

  const offset = (worldTime - localTime) / 1000 / 60;

  return offset;
}

/**
 * If i am in GMT-3,
 * and i want to send 1200 to the API,
 * i need to send 1500;
 *
 * Input: 1200
 * Output: 1500
 * it should determine my timezone and
 * put it in the right format of the
 * Berlin timezone
 */
export function hhmmToEuropeTimeZone(time: string): string {
  const berlinTimeZoneOffset =
    new Date().getTimezoneOffset() - getTimeOffsetMinutes('Europe/Berlin');
  const hh = parseInt(time.substring(0, 2));
  const mm = parseInt(time.substring(2));

  const date = new Date();
  date.setHours(hh, mm, 0, 0);

  date.setMinutes(date.getMinutes() + berlinTimeZoneOffset);

  return date.toLocaleTimeString('en-GB').substring(0, 5).replace(':', '');
}
